import React, { useCallback, useContext, useEffect, useState } from "react";
import { Alert, Box, CircularProgress, Tab, Tabs } from "@mui/material";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import {
  leagueCustomerFeaturesRoute,
  leagueCustomerFinancialIncomeSummaryRoute,
  leagueCustomerTournamentsRoute,
} from "network/api-routes";
import { useGestionCustomer } from "hooks/useGestionCustomer";
import GestionFinancialOverview from "./financial/GestionFinancialOverview";
import GestionFinancialTransactions from "./financial/GestionFinancialTransactions";
import { financialsTabFromSearch } from "./financial/financials-tabs";
import GestionFinancialPriceList from "./financial/GestionFinancialPriceList";
import GestionFinancialAccountsCard from "./financial/GestionFinancialAccountsCard";
import GestionMembershipSettings from "./GestionMembershipSettings";
import { formatSubscriptionStatus } from "utils/formatSubscriptionStatus";
import {
  financialAccountHref,
  financialAccountHrefFromSearch,
} from "./financial/financial-account-deep-link";

const GestionFinancials = () => {
  const { customerId, customerName } = useGestionCustomer();
  const { user } = useContext(UserContext);
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [financialEnabled, setFinancialEnabled] = useState(false);
  const [featureMeta, setFeatureMeta] = useState(null);
  const [tab, setTab] = useState(() =>
    financialsTabFromSearch(location.search),
  );
  const [incomeSummary, setIncomeSummary] = useState(null);
  // Bumped after any reload so the paginated balance/transaction tables refetch.
  const [reloadKey, setReloadKey] = useState(0);
  const [tournaments, setTournaments] = useState([]);

  const handleError = useCallback(
    (error) => {
      enqueueSnackbar(
        error?.response?.data?.message ||
          intl.formatMessage({ id: "gestion.error.generic" }),
        {
          variant: "error",
        },
      );
    },
    [enqueueSnackbar, intl],
  );

  const loadFinancialData = useCallback(() => {
    if (!customerId || !user) return Promise.resolve();
    return Promise.all([
      performRequest(
        "GET",
        leagueCustomerFinancialIncomeSummaryRoute(customerId),
        {},
        true,
        user,
      ),
      performRequest(
        "GET",
        leagueCustomerTournamentsRoute(customerId),
        { page_size: 100 },
        true,
        user,
      ),
    ])
      .then(([incomeResponse, tournamentsResponse]) => {
        setIncomeSummary(incomeResponse.data);
        setReloadKey((key) => key + 1);
        const tournamentData =
          tournamentsResponse.data?.results ||
          tournamentsResponse.data?.data ||
          tournamentsResponse.data ||
          [];
        setTournaments(Array.isArray(tournamentData) ? tournamentData : []);
      })
      .catch((error) => {
        if (error?.response?.status !== 402) {
          handleError(error);
        }
      });
  }, [customerId, handleError, user]);

  const load = useCallback(() => {
    if (!customerId) return;
    setLoading(true);
    performRequest(
      "GET",
      leagueCustomerFeaturesRoute(customerId),
      {},
      true,
      user,
    )
      .then((response) => {
        const financial = response.data?.financial ?? {};
        setFinancialEnabled(Boolean(financial.enabled));
        setFeatureMeta(financial);
        if (financial.enabled) {
          return loadFinancialData();
        }
        setIncomeSummary(null);
        setTournaments([]);
        return null;
      })
      .catch(handleError)
      .finally(() => setLoading(false));
  }, [customerId, handleError, loadFinancialData, user]);

  useEffect(() => {
    load();
  }, [load]);

  const handleOpenAccount = (account) => {
    const href = financialAccountHref(customerId, account);
    if (href) history.push(href);
  };

  const legacyAccountHref = financialAccountHrefFromSearch(
    customerId,
    location.search,
  );

  if (!customerId) return null;

  if (legacyAccountHref) {
    return <Redirect to={legacyAccountHref} />;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{ width: "100%", maxWidth: "lg", mx: "auto", p: { xs: 3, md: 4 } }}
    >
      <RevaText variant="h4">
        {intl.formatMessage(
          { id: "gestion.financials.title" },
          { name: customerName || "" },
        )}
      </RevaText>
      <RevaText variant="body1" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        {intl.formatMessage({ id: "gestion.financials.subtitle" })}
      </RevaText>

      {!financialEnabled && (
        <>
          <Alert severity="info" sx={{ mt: 3 }}>
            {intl.formatMessage({ id: "gestion.financials.paywall" })}
            {featureMeta?.subscription_status && (
              <RevaText variant="body2" sx={{ mt: 1 }}>
                {intl.formatMessage(
                  { id: "gestion.financials.subscriptionStatus" },
                  {
                    status: formatSubscriptionStatus(
                      intl,
                      featureMeta.subscription_status,
                    ),
                  },
                )}
              </RevaText>
            )}
          </Alert>
        </>
      )}

      {financialEnabled && (
        <Box sx={{ mt: 3 }}>
          <Tabs
            value={tab}
            onChange={(_event, value) => setTab(value)}
            sx={{ mb: 3 }}
          >
            <Tab
              label={intl.formatMessage({
                id: "gestion.financials.tabGeneral",
              })}
            />
            <Tab
              label={intl.formatMessage({
                id: "gestion.financials.tabTransactions",
              })}
            />
            <Tab
              label={intl.formatMessage({
                id: "gestion.financials.tabPriceList",
              })}
            />
            <Tab
              label={intl.formatMessage({
                id: "gestion.financials.tabMembership",
              })}
            />
          </Tabs>

          {tab === 0 && (
            <GestionFinancialOverview
              customerId={customerId}
              user={user}
              reloadKey={reloadKey}
              onError={handleError}
              incomeSummary={incomeSummary}
              onOpenAccount={handleOpenAccount}
              accountSummary={
                <GestionFinancialAccountsCard
                  customerId={customerId}
                  user={user}
                  onUpdated={loadFinancialData}
                  section="all"
                  embedded
                />
              }
            />
          )}
          {tab === 1 && (
            <GestionFinancialTransactions
              customerId={customerId}
              user={user}
              reloadKey={reloadKey}
              onError={handleError}
            />
          )}
          {tab === 2 && (
            <GestionFinancialPriceList
              currency={incomeSummary?.overview?.currency || ""}
              customerId={customerId}
              tournaments={tournaments}
            />
          )}
          {tab === 3 && (
            <GestionMembershipSettings
              embedded
              accountSelector={
                <GestionFinancialAccountsCard
                  customerId={customerId}
                  user={user}
                  onUpdated={loadFinancialData}
                  section="gestion"
                  embedded
                  compact
                />
              }
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default GestionFinancials;
